// Category + item CRUD on one menu. The simplest shape that still
// exercises the slice-4 endpoints: list categories with their items,
// one form to add a category, one form to add an item under each
// category, one inline edit for an existing item.

import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../api/client.js';
import type {
  MenuCategory,
  MenuDetail,
  MenuItemRow,
  MeResponse,
  Permission,
} from '../api/types.js';
import { PublishPanel } from './components/PublishPanel.js';
import { ItemMediaEditor } from './components/ItemMediaEditor.js';
import { ItemSizesEditor } from './components/ItemSizesEditor.js';
import { ItemPhotoEditor } from './components/ItemPhotoEditor.js';
import { BannersPanel } from './components/BannersPanel.js';
import { OffersPanel } from './components/OffersPanel.js';
import { hasPermission } from '../auth/permissions.js';

const DEFAULT_LANG = 'en';

interface TaxProfileView {
  readonly version: number;
  readonly definition: {
    readonly classes?: readonly {
      readonly code: string;
      readonly mode: string;
      readonly gstBp: number;
      readonly cessBp: number;
    }[];
  };
}

interface TaxClassOption {
  readonly code: string;
  readonly label: string;
}

/** "GST5E · 5% GST, tax added" for the picker; basis points shown as percent. */
function taxClassesOf(profile: TaxProfileView | undefined): readonly TaxClassOption[] {
  return (profile?.definition.classes ?? []).map((c) => {
    const pct = (bp: number): string => String(bp / 100);
    const cess = c.cessBp > 0 ? ` + ${pct(c.cessBp)}% cess` : '';
    const mode = c.mode === 'inclusive' ? 'included in price' : 'added to price';
    return { code: c.code, label: `${c.code} · ${pct(c.gstBp)}% GST${cess}, ${mode}` };
  });
}

function taxClassLabel(code: string, options: readonly TaxClassOption[]): string {
  return options.find((o) => o.code === code) ? code : `${code} (unknown)`;
}

// The menu editor only has orgId in the URL — the menu's outlet
// isn't exposed. For slice 16 we scope permissions by orgId alone:
// if ANY of this user's memberships in this org grants `menu:edit`,
// we render the editing forms. The server is still authoritative
// (PermissionGuard checks the specific scope on every write).
function useOrgPermissions(orgId: string | undefined): readonly Permission[] {
  const qc = useQueryClient();
  const me = qc.getQueryData<MeResponse>(['me']);
  if (!me || !orgId) return [];
  const match = me.memberships.find((m) => m.organization.id === orgId);
  return match?.permissions ?? [];
}

export function MenuEditorPage(): JSX.Element {
  const { orgId = '', menuId = '' } = useParams();
  const qc = useQueryClient();
  const detailKey = ['menu-detail', orgId, menuId] as const;

  // Deep-link safety: HomePage populates ['me'], but a tab refresh on
  // this URL has no cached me. Fire the same key; TanStack dedupes.
  useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });
  const permissions = useOrgPermissions(orgId);
  const canEdit = hasPermission(permissions, 'menu:edit');

  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => apiFetch<MenuDetail>(`/organizations/${orgId}/menus/${menuId}`),
  });

  // The tax classes an item may use are the newest tax profile's (slice 59).
  // Reading them needs `pos:read`; without it, or before a profile exists, the
  // picker is simply not drawn and items stay on the outlet's default.
  const outletId = detail.data?.outletId ?? '';
  const taxProfiles = useQuery({
    queryKey: ['tax-classes', orgId, outletId],
    enabled: canEdit && outletId !== '',
    retry: false,
    queryFn: () =>
      apiFetch<{ profiles: readonly TaxProfileView[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/tax-profiles`,
      ),
  });
  const taxClasses = taxClassesOf(taxProfiles.data?.profiles[0]);

  const addCategory = useMutation({
    mutationFn: (name: string) =>
      apiFetch<MenuCategory>(`/organizations/${orgId}/menus/${menuId}/categories`, {
        method: 'POST',
        body: { translations: [{ languageCode: DEFAULT_LANG, name }] },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey }),
  });

  const [categoryName, setCategoryName] = useState('');

  // Which categories the owner has folded away. Kept per menu in this browser only
  // (a convenience, not menu data): a long menu reopens the way it was left.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => loadCollapsed(menuId));
  function setCollapsedAndSave(next: ReadonlySet<string>): void {
    setCollapsed(next);
    saveCollapsed(menuId, next);
  }
  function toggleCategory(id: string): void {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsedAndSave(next);
  }

  function onAddCategory(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    addCategory.mutate(categoryName.trim(), {
      onSuccess: () => setCategoryName(''),
    });
  }

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>{detail.data?.name ?? 'Menu'}</h1>
          <p className="muted">
            <Link to="/home">← outlets</Link>
          </p>
        </div>
      </header>

      {detail.isLoading ? <p>Loading…</p> : null}
      {detail.error ? <p className="error">Could not load menu.</p> : null}

      {detail.data ? (
        <>
          <PublishPanel
            orgId={orgId}
            outletId={detail.data.outletId}
            menuId={menuId}
            menuVersion={detail.data.version}
            canPublish={hasPermission(permissions, 'menu:publish')}
            detailKey={detailKey as unknown as readonly unknown[]}
          />

          {canEdit ? (
            <section className="card">
              <h2>Add category</h2>
              <form onSubmit={onAddCategory}>
                <label htmlFor="cat-name">Name</label>
                <input
                  id="cat-name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder="Starters"
                  required
                />
                <button type="submit" disabled={addCategory.isPending}>
                  {addCategory.isPending ? 'Adding…' : 'Add category'}
                </button>
                {addCategory.error ? (
                  <p className="error">
                    {addCategory.error instanceof ApiError
                      ? addCategory.error.message
                      : 'could not add'}
                  </p>
                ) : null}
              </form>
            </section>
          ) : null}

          {detail.data.categories.length === 0 ? (
            <p className="muted">
              {canEdit ? 'No categories yet. Add one above.' : 'This menu has no categories yet.'}
            </p>
          ) : null}

          {detail.data.categories.length > 1 ? (
            <div className="row category-controls">
              <button
                type="button"
                className="button secondary"
                onClick={() => setCollapsedAndSave(new Set())}
              >
                Expand all
              </button>
              <button
                type="button"
                className="button secondary"
                onClick={() =>
                  setCollapsedAndSave(new Set(detail.data.categories.map((c) => c.id)))
                }
              >
                Collapse all
              </button>
            </div>
          ) : null}

          {detail.data.categories.map((c) => (
            <CategoryBlock
              key={c.id}
              isCollapsed={collapsed.has(c.id)}
              onToggle={() => toggleCategory(c.id)}
              orgId={orgId}
              menuId={menuId}
              detailKey={detailKey as unknown as readonly unknown[]}
              category={c}
              canEdit={canEdit}
              taxClasses={taxClasses}
            />
          ))}

          <BannersPanel orgId={orgId} menuId={menuId} menu={detail.data} canEdit={canEdit} />
          <OffersPanel orgId={orgId} menuId={menuId} menu={detail.data} canEdit={canEdit} />
        </>
      ) : null}
    </main>
  );
}

function CategoryBlock(props: {
  readonly orgId: string;
  readonly menuId: string;
  readonly detailKey: readonly unknown[];
  readonly category: MenuCategory;
  readonly canEdit: boolean;
  readonly taxClasses: readonly TaxClassOption[];
  readonly isCollapsed: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  const { orgId, menuId, detailKey, category, canEdit, taxClasses, isCollapsed, onToggle } = props;
  const qc = useQueryClient();

  const addItem = useMutation({
    mutationFn: (body: {
      name: string;
      pricePaise: number;
      isVeg: boolean;
      isBestseller: boolean;
      description: string;
    }) =>
      apiFetch<MenuItemRow>(`/organizations/${orgId}/menus/${menuId}/items`, {
        method: 'POST',
        body: {
          categoryId: category.id,
          pricePaise: body.pricePaise,
          isVeg: body.isVeg,
          isBestseller: body.isBestseller,
          isAvailable: true,
          translations: [
            {
              languageCode: DEFAULT_LANG,
              name: body.name,
              ...(body.description ? { description: body.description } : {}),
            },
          ],
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey }),
  });

  const categoryLabel =
    category.translations.find((t) => t.languageCode === DEFAULT_LANG)?.name ??
    category.translations[0]?.name ??
    '(untitled)';

  // Rename keeps any other language's name as it is; only the default one changes.
  const renameCategory = useMutation({
    mutationFn: (name: string) => {
      const others = category.translations.filter((t) => t.languageCode !== DEFAULT_LANG);
      return apiFetch<unknown>(
        `/organizations/${orgId}/menus/${menuId}/categories/${category.id}`,
        {
          method: 'PUT',
          body: { translations: [{ languageCode: DEFAULT_LANG, name }, ...others] },
        },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey }),
  });
  const deleteCategory = useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`/organizations/${orgId}/menus/${menuId}/categories/${category.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey }),
  });
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(categoryLabel);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  function onRename(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const name = nameDraft.trim();
    if (name === '') return;
    renameCategory.mutate(name, { onSuccess: () => setRenaming(false) });
  }

  const [itemName, setItemName] = useState('');
  const [itemRupees, setItemRupees] = useState('');
  const [itemDesc, setItemDesc] = useState('');
  const [itemVeg, setItemVeg] = useState(true);
  const [itemBest, setItemBest] = useState(false);

  function onAddItem(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const rupees = Number(itemRupees);
    if (!Number.isFinite(rupees) || rupees < 0) return;
    addItem.mutate(
      {
        name: itemName.trim(),
        pricePaise: Math.round(rupees * 100),
        isVeg: itemVeg,
        isBestseller: itemBest,
        description: itemDesc.trim(),
      },
      {
        onSuccess: () => {
          setItemName('');
          setItemRupees('');
          setItemDesc('');
          setItemVeg(true);
          setItemBest(false);
        },
      },
    );
  }

  return (
    <section className="card">
      <h2 className="category-heading">
        <button
          type="button"
          className="category-toggle"
          aria-expanded={!isCollapsed}
          aria-label={`${categoryLabel}, ${category.items.length} ${
            category.items.length === 1 ? 'item' : 'items'
          }`}
          onClick={onToggle}
        >
          <span aria-hidden="true" className="category-chevron">
            {isCollapsed ? '▸' : '▾'}
          </span>
          <span>{categoryLabel}</span>
          <span className="muted category-count">
            {category.items.length} {category.items.length === 1 ? 'item' : 'items'}
          </span>
        </button>
      </h2>
      {/* Folded, not removed: nothing reloads when it is opened again. */}
      <div hidden={isCollapsed}>
        {canEdit ? (
          <div className="category-actions">
            {renaming ? (
              <form onSubmit={onRename} className="inline-form">
                <input
                  aria-label="category name"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  required
                />
                <button type="submit" disabled={renameCategory.isPending}>
                  {renameCategory.isPending ? 'Saving…' : 'Save name'}
                </button>
                <button type="button" onClick={() => setRenaming(false)}>
                  Cancel
                </button>
              </form>
            ) : confirmingDelete ? (
              <div className="row" role="alertdialog" aria-label={`Delete ${categoryLabel}`}>
                <span>
                  Delete the category “{categoryLabel}”? It disappears from the public menu after
                  you publish.
                </span>
                <button
                  type="button"
                  disabled={deleteCategory.isPending}
                  onClick={() => deleteCategory.mutate()}
                >
                  {deleteCategory.isPending ? 'Deleting…' : 'Yes, delete'}
                </button>
                <button type="button" onClick={() => setConfirmingDelete(false)}>
                  Keep
                </button>
              </div>
            ) : (
              <div className="row">
                <button
                  type="button"
                  onClick={() => {
                    setNameDraft(categoryLabel);
                    setRenaming(true);
                  }}
                >
                  Rename
                </button>
                <button
                  type="button"
                  disabled={category.items.length > 0}
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete category
                </button>
                {category.items.length > 0 ? (
                  <span className="muted">Delete or move its dishes first.</span>
                ) : null}
              </div>
            )}
            {renameCategory.error ? (
              <p className="error">{apiMessage(renameCategory.error, 'could not rename')}</p>
            ) : null}
            {deleteCategory.error ? (
              <p className="error">{apiMessage(deleteCategory.error, 'could not delete')}</p>
            ) : null}
          </div>
        ) : null}
        {category.items.length === 0 ? (
          <p className="muted">{canEdit ? 'No items yet.' : 'No items in this category.'}</p>
        ) : (
          <ul className="item-list">
            {category.items.map((i) => (
              <ItemRow
                key={i.id}
                orgId={orgId}
                menuId={menuId}
                detailKey={detailKey}
                item={i}
                canEdit={canEdit}
                taxClasses={taxClasses}
              />
            ))}
          </ul>
        )}
        {canEdit ? (
          <>
            <form onSubmit={onAddItem} className="inline-form">
              <input
                aria-label="item name"
                placeholder="Item name"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                required
              />
              <input
                aria-label="price in rupees"
                type="number"
                min="0"
                step="0.01"
                placeholder="₹ price"
                value={itemRupees}
                onChange={(e) => setItemRupees(e.target.value)}
                required
              />
              <input
                aria-label="description"
                placeholder="Description (optional)"
                value={itemDesc}
                onChange={(e) => setItemDesc(e.target.value)}
              />
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={itemVeg}
                  onChange={(e) => setItemVeg(e.target.checked)}
                />
                veg
              </label>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={itemBest}
                  onChange={(e) => setItemBest(e.target.checked)}
                />
                best seller
              </label>
              <button type="submit" disabled={addItem.isPending}>
                {addItem.isPending ? 'Adding…' : 'Add item'}
              </button>
            </form>
            {addItem.error ? (
              <p className="error">
                {addItem.error instanceof ApiError ? addItem.error.message : 'could not add item'}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

const COLLAPSED_KEY = (menuId: string): string => `tabletop.menu-collapsed.${menuId}`;

function loadCollapsed(menuId: string): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_KEY(menuId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function saveCollapsed(menuId: string, ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY(menuId), JSON.stringify([...ids]));
  } catch {
    // Storage blocked: the folds simply are not remembered.
  }
}

function ItemRow(props: {
  readonly orgId: string;
  readonly menuId: string;
  readonly detailKey: readonly unknown[];
  readonly item: MenuItemRow;
  readonly canEdit: boolean;
  readonly taxClasses: readonly TaxClassOption[];
}): JSX.Element {
  const { orgId, menuId, detailKey, item, canEdit, taxClasses } = props;
  const qc = useQueryClient();

  const deleteItem = useMutation({
    mutationFn: () =>
      apiFetch<unknown>(`/organizations/${orgId}/menus/${menuId}/items/${item.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey }),
  });
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const updateItem = useMutation({
    mutationFn: (body: {
      pricePaise?: number;
      isAvailable?: boolean;
      isBestseller?: boolean;
      taxClass?: string | null;
      translations?: readonly { languageCode: string; name: string; description: string }[];
    }) =>
      apiFetch<MenuItemRow>(`/organizations/${orgId}/menus/${menuId}/items/${item.id}`, {
        method: 'PUT',
        body: { ...body, expectedVersion: item.version },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey }),
  });

  const translation =
    item.translations.find((t) => t.languageCode === DEFAULT_LANG) ?? item.translations[0];
  const sizes = item.options ?? [];
  const [editing, setEditing] = useState(false);
  const [rupees, setRupees] = useState((item.pricePaise / 100).toFixed(2));
  const [nameDraft, setNameDraft] = useState(translation?.name ?? '');
  const [descDraft, setDescDraft] = useState(translation?.description ?? '');

  function onSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const r = Number(rupees);
    const name = nameDraft.trim();
    // A dish with sizes has its price in the sizes; the form does not touch it.
    if (name === '' || (sizes.length === 0 && (!Number.isFinite(r) || r < 0))) return;
    // Replace the default-language name and description; keep any other language as it is.
    const others = item.translations
      .filter((t) => t.languageCode !== DEFAULT_LANG)
      .map((t) => ({
        languageCode: t.languageCode,
        name: t.name,
        description: t.description ?? '',
      }));
    updateItem.mutate(
      {
        ...(sizes.length === 0 ? { pricePaise: Math.round(r * 100) } : {}),
        translations: [
          { languageCode: DEFAULT_LANG, name, description: descDraft.trim() },
          ...others,
        ],
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  function onTaxClass(value: string): void {
    updateItem.mutate({ taxClass: value === '' ? null : value });
  }

  function toggleAvailable(): void {
    updateItem.mutate({ isAvailable: !item.isAvailable });
  }

  function toggleBestseller(): void {
    updateItem.mutate({ isBestseller: !item.isBestseller });
  }

  return (
    <li className="item-row">
      <div>
        <strong>{translation?.name ?? '(untitled)'}</strong>
        {translation?.description ? (
          <span className="muted"> — {translation.description}</span>
        ) : null}
        <br />
        <span className="muted">
          {sizes.length > 0
            ? sizes.map((o) => `${o.label} ₹${(o.pricePaise / 100).toFixed(2)}`).join(' · ')
            : `₹${(item.pricePaise / 100).toFixed(2)}`}{' '}
          · {item.isVeg ? 'veg' : 'non-veg'} · {item.isAvailable ? 'available' : 'hidden'}
          {item.isBestseller ? ' · best seller' : ''}
          {item.taxClass ? ` · tax ${taxClassLabel(item.taxClass, taxClasses)}` : ''} · v
          {item.version}
        </span>
      </div>
      {canEdit ? (
        <div className="row">
          <button
            type="button"
            onClick={() => {
              setNameDraft(translation?.name ?? '');
              setDescDraft(translation?.description ?? '');
              setRupees((item.pricePaise / 100).toFixed(2));
              setEditing((e) => !e);
            }}
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button type="button" onClick={toggleAvailable} disabled={updateItem.isPending}>
            {item.isAvailable ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            aria-pressed={item.isBestseller === true}
            onClick={toggleBestseller}
            disabled={updateItem.isPending}
          >
            {item.isBestseller ? '★ Best seller: on' : '☆ Mark best seller'}
          </button>
          <button type="button" onClick={() => setConfirmingDelete(true)}>
            Delete
          </button>
        </div>
      ) : null}
      {canEdit && confirmingDelete ? (
        <div
          className="row"
          role="alertdialog"
          aria-label={`Delete ${translation?.name ?? 'dish'}`}
        >
          <span>
            Delete “{translation?.name ?? 'this dish'}”? It disappears from the public menu after
            you publish. To only take it off for now, use Hide instead.
          </span>
          <button type="button" disabled={deleteItem.isPending} onClick={() => deleteItem.mutate()}>
            {deleteItem.isPending ? 'Deleting…' : 'Yes, delete'}
          </button>
          <button type="button" onClick={() => setConfirmingDelete(false)}>
            Keep
          </button>
        </div>
      ) : null}
      {deleteItem.error ? (
        <p className="error">{apiMessage(deleteItem.error, 'could not delete')}</p>
      ) : null}
      {canEdit && taxClasses.length > 0 ? (
        <label className="inline-form">
          <span className="muted">Tax rate</span>
          <select
            aria-label={`tax rate for ${translation?.name ?? 'item'}`}
            value={item.taxClass ?? ''}
            disabled={updateItem.isPending}
            onChange={(e) => onTaxClass(e.target.value)}
          >
            <option value="">Outlet default</option>
            {item.taxClass && !taxClasses.some((c) => c.code === item.taxClass) ? (
              <option value={item.taxClass}>{item.taxClass} (not in the tax settings)</option>
            ) : null}
            {taxClasses.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {canEdit && editing ? (
        <form onSubmit={onSave} className="inline-form">
          <input
            aria-label="dish name"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            required
          />
          <input
            aria-label="dish description"
            placeholder="Description (optional)"
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
          />
          {sizes.length === 0 ? (
            <input
              aria-label="new price"
              type="number"
              min="0"
              step="0.01"
              value={rupees}
              onChange={(e) => setRupees(e.target.value)}
            />
          ) : (
            <span className="muted">The price comes from the sizes.</span>
          )}
          <button type="submit" disabled={updateItem.isPending}>
            Save
          </button>
        </form>
      ) : null}
      {updateItem.error ? (
        <p className="error">
          {updateItem.error instanceof ApiError ? updateItem.error.message : 'update failed'}
        </p>
      ) : null}
      {canEdit ? (
        <ItemSizesEditor
          orgId={orgId}
          menuId={menuId}
          detailKey={detailKey}
          item={item}
          itemName={translation?.name ?? 'dish'}
        />
      ) : null}
      {canEdit ? (
        <ItemPhotoEditor orgId={orgId} menuId={menuId} detailKey={detailKey} item={item} />
      ) : null}
      {canEdit ? (
        <ItemMediaEditor
          orgId={orgId}
          menuId={menuId}
          itemId={item.id}
          itemName={translation?.name ?? 'dish'}
        />
      ) : null}
    </li>
  );
}

// The API's own sentence when it has one (it says why a delete was refused),
// a plain fallback otherwise.
function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'You do not have permission to do that.';
    if (err.status === 0) return 'Network error. Check your connection.';
    return err.message || fallback;
  }
  return fallback;
}
